import { useState, useEffect, useCallback } from 'react';
import { BellIcon, CheckIcon, AtSymbolIcon, UserCircleIcon, ChatBubbleLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { Navbar } from '@/components/layout/Navbar';
import { notificationApi, initiativeApi, Notification } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/contexts/AuthContext';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';
import type { Initiative } from '@/types';

const PAGE_SIZE = 50;

type NotificationTab = 'all' | 'mentions' | 'assigned';

function typeForTab(tab: NotificationTab): string | undefined {
  if (tab === 'mentions') return 'mention';
  if (tab === 'assigned') return 'assigned';
  return undefined;
}

function groupByDate(notifications: Notification[]): Record<string, Notification[]> {
  return notifications.reduce((acc, n) => {
    const date = new Date(n.createdAt).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(n);
    return acc;
  }, {} as Record<string, Notification[]>);
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Prefer the live relation title (initiative may have been renamed since); fall back to the
// snapshot, which survives initiative deletion.
function titleFor(n: Notification): string {
  return n.initiative?.title || n.initiativeTitle || '';
}

function getInitiativePath(initiatives: Initiative[], initiativeId: string | null): string {
  if (!initiativeId) return '';
  const init = initiatives.find(i => i.id === initiativeId) as any;
  const productName = init?.subProduct?.product?.name;
  const spName = init?.subProduct?.name;
  if (productName && spName) return `${productName} › ${spName}`;
  return spName || '';
}

export function NotificationsPage() {
  const labs = useLabsFeatures();
  const { user } = useAuth();
  const { openViewInitiativeModal, initiatives, setUnreadNotificationCount, unreadNotificationCount } = useAppStore();

  const [tab, setTab] = useState<NotificationTab>('all');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(user?.emailNotificationsEnabled ?? true);
  const [emailSaving, setEmailSaving] = useState(false);

  const handleEmailToggle = async () => {
    const next = !emailEnabled;
    setEmailEnabled(next);
    setEmailSaving(true);
    try {
      await notificationApi.updatePreferences({ emailNotificationsEnabled: next });
    } catch {
      setEmailEnabled(!next); // revert on failure
    } finally {
      setEmailSaving(false);
    }
  };

  const loadInitial = useCallback(async (activeTab: NotificationTab) => {
    setLoading(true);
    setNotifications([]);
    setHasMore(false);
    setTotal(0);
    try {
      const { notifications: data, total: t, unread, hasMore: more } =
        await notificationApi.list(PAGE_SIZE, 0, typeForTab(activeTab));
      setNotifications(data);
      setTotal(t);
      setHasMore(more);
      setUnreadNotificationCount(unread);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [setUnreadNotificationCount]);

  useEffect(() => { loadInitial(tab); }, [tab, loadInitial]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const { notifications: data, hasMore: more } =
        await notificationApi.list(PAGE_SIZE, notifications.length, typeForTab(tab));
      setNotifications((prev) => [...prev, ...data]);
      setHasMore(more);
    } catch (err) {
      console.error('Failed to load more notifications:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationApi.readAll();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadNotificationCount(0);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleMarkOneRead = async (id: string) => {
    await notificationApi.readOne(id).catch(() => {});
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setUnreadNotificationCount(Math.max(0, unreadNotificationCount - 1));
  };

  const handleRowClick = async (n: Notification) => {
    if (!n.isRead) handleMarkOneRead(n.id);
    if (n.initiativeId) {
      let initiative = initiatives.find((i) => i.id === n.initiativeId);
      if (!initiative) {
        try { initiative = await initiativeApi.getById(n.initiativeId); } catch {}
      }
      if (initiative) openViewInitiativeModal(initiative as any, 'comments');
    }
  };

  const tabClass = (t: NotificationTab) =>
    `flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
    }`;

  const renderCard = (n: Notification) => {
    const path = getInitiativePath(initiatives, n.initiativeId);
    const isUnread = !n.isRead;
    const cardClass = `w-full text-left rounded-lg shadow-sm p-4 hover:shadow-md transition-all ${
      isUnread ? 'bg-blue-50/40' : 'bg-white'
    }`;

    if (n.type === 'assigned') {
      return (
        <button key={n.id} onClick={() => handleRowClick(n)} className={cardClass}>
          <div className="flex items-start gap-3">
            <span className={`flex-shrink-0 mt-1.5 w-2 h-2 rounded-full ${isUnread ? 'bg-blue-500' : 'bg-transparent'}`} />
            <UserCircleIcon
              className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5"
              title="Assigned to you"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-medium text-gray-900 truncate">
                  {titleFor(n)}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                  {formatDateTime(n.createdAt)}
                </span>
              </div>
              {path && <p className="text-sm text-gray-500">{path}</p>}
            </div>
          </div>
        </button>
      );
    }

    // mention (default)
    return (
      <button key={n.id} onClick={() => handleRowClick(n)} className={cardClass}>
        <div className="flex items-start gap-3">
          <span className={`flex-shrink-0 mt-1.5 w-2 h-2 rounded-full ${isUnread ? 'bg-blue-500' : 'bg-transparent'}`} />
          <ChatBubbleLeftIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <p className="text-sm">
                <span className="font-semibold text-blue-600">{n.actorName}</span>
                <span className="text-gray-600"> mentioned you</span>
              </p>
              <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
                {formatDateTime(n.createdAt)}
              </span>
            </div>
            {titleFor(n) && (
              <p className="text-sm text-gray-700 truncate mb-0.5">{titleFor(n)}</p>
            )}
            {n.commentPreview && (
              <p className="text-sm text-gray-500 truncate italic mb-0.5">"{n.commentPreview}"</p>
            )}
            {path && <p className="text-sm text-gray-500">{path}</p>}
          </div>
        </div>
      </button>
    );
  };

  if (!labs.isEnabled('notifications')) {
    return (
      <div className="flex flex-col h-screen bg-gray-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <BellIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Notifications are not enabled for this workspace.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />
      <main className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
              {unreadNotificationCount > 0 && (
                <p className="text-sm text-gray-500 mt-1">{unreadNotificationCount} unread</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleEmailToggle}
                disabled={emailSaving}
                title={emailEnabled ? 'Turn off email notifications' : 'Turn on email notifications'}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
                  emailEnabled ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-400 hover:bg-gray-100'
                }`}
              >
                <EnvelopeIcon className="w-4 h-4" />
                Email {emailEnabled ? 'on' : 'off'}
              </button>
              {unreadNotificationCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                >
                  <CheckIcon className="w-4 h-4" />
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
            <button onClick={() => setTab('all')} className={tabClass('all')}>
              <BellIcon className="w-4 h-4" />
              All
            </button>
            <button onClick={() => setTab('mentions')} className={tabClass('mentions')}>
              <AtSymbolIcon className="w-4 h-4" />
              Mentions
            </button>
            <button onClick={() => setTab('assigned')} className={tabClass('assigned')}>
              <UserCircleIcon className="w-4 h-4" />
              Assigned to you
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <BellIcon className="w-10 h-10 mb-3 opacity-50" />
              <p className="text-sm">
                {tab === 'mentions' ? 'No mention notifications' :
                 tab === 'assigned' ? 'No assignment notifications' :
                 'No notifications yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="text-sm text-gray-500 mb-4">
                Showing {notifications.length} of {total} notifications
              </div>

              <div className="space-y-8">
                {Object.entries(groupByDate(notifications)).map(([date, items]) => (
                  <div key={date}>
                    <h2 className="text-sm font-medium text-gray-500 mb-3">{date}</h2>
                    <div className="space-y-3">
                      {items.map((n) => renderCard(n))}
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                        Loading...
                      </span>
                    ) : 'Load More'}
                  </button>
                </div>
              )}

              {!hasMore && notifications.length > 0 && (
                <div className="mt-8 text-center text-sm text-gray-400">
                  No more notifications
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
